import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ArrowLeft,
  Settings,
  User,
  Shield,
  Clock,
  LogOut,
  CheckCircle2,
  AlertCircle,
  Info
} from 'lucide-react';

const SettingsPage = ({ onBack }) => {
  const { user, logout, api } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await api.get('/auth/session');
        setSession(response.data);
      } catch (err) {
        setError('Failed to fetch session info');
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
  }, [api]);

  const handleLogout = () => {
    logout();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b]" data-testid="settings-page">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onBack}
                className="text-zinc-400 hover:text-zinc-100"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <Separator orientation="vertical" className="h-4 bg-zinc-700" />
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-zinc-500" />
                <span className="text-sm font-medium text-zinc-100">Settings</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <Alert variant="destructive" className="mb-6 bg-red-950/50 border-red-900">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-6">
          {/* Session Status */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-zinc-400" />
                <CardTitle className="text-zinc-100">Session Status</CardTitle>
              </div>
              <CardDescription className="text-zinc-500">
                Current authentication session information
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center gap-2 text-zinc-500">
                  <div className="w-4 h-4 border-2 border-zinc-700 border-t-zinc-400 rounded-full animate-spin" />
                  Loading session...
                </div>
              ) : session ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    <span className="text-zinc-200">Session Active</span>
                    <Badge className="bg-emerald-600 text-white">Valid</Badge>
                  </div>
                  <Separator className="bg-zinc-800" />
                  <div className="grid gap-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Session Type</span>
                      <span className="text-zinc-200">JWT (72h expiry)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Last Login</span>
                      <span className="text-zinc-200">{formatDate(session.last_login)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-zinc-500">
                  <AlertCircle className="w-4 h-4" />
                  Unable to fetch session status
                </div>
              )}
            </CardContent>
          </Card>

          {/* Account Info (Read-Only) */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-zinc-400" />
                <CardTitle className="text-zinc-100">Account Information</CardTitle>
              </div>
              <CardDescription className="text-zinc-500">
                Your account details (read-only)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid gap-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">User ID</span>
                    <span className="text-zinc-300 font-mono text-xs">{user?.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Username</span>
                    <span className="text-zinc-200">{user?.username}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Email</span>
                    <span className="text-zinc-200">{user?.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Account Created</span>
                    <span className="text-zinc-200">{formatDate(user?.created_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Account Status</span>
                    <Badge className={user?.is_active ? 'bg-emerald-600' : 'bg-red-600'}>
                      {user?.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Not Implemented Features */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Info className="w-5 h-5 text-zinc-400" />
                <CardTitle className="text-zinc-100">Not Implemented (v1)</CardTitle>
              </div>
              <CardDescription className="text-zinc-500">
                These features are planned but not available in v1
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm text-zinc-500">
                <div className="flex items-center gap-2 p-2 rounded bg-zinc-800/50">
                  <Clock className="w-4 h-4" />
                  <span>Password Change</span>
                  <Badge variant="outline" className="ml-auto border-zinc-700 text-zinc-500">Planned</Badge>
                </div>
                <div className="flex items-center gap-2 p-2 rounded bg-zinc-800/50">
                  <Clock className="w-4 h-4" />
                  <span>Email Change</span>
                  <Badge variant="outline" className="ml-auto border-zinc-700 text-zinc-500">Planned</Badge>
                </div>
                <div className="flex items-center gap-2 p-2 rounded bg-zinc-800/50">
                  <Clock className="w-4 h-4" />
                  <span>Account Deletion</span>
                  <Badge variant="outline" className="ml-auto border-zinc-700 text-zinc-500">Planned</Badge>
                </div>
                <div className="flex items-center gap-2 p-2 rounded bg-zinc-800/50">
                  <Clock className="w-4 h-4" />
                  <span>API Keys</span>
                  <Badge variant="outline" className="ml-auto border-zinc-700 text-zinc-500">Planned</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Logout */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <div className="flex items-center gap-2">
                <LogOut className="w-5 h-5 text-zinc-400" />
                <CardTitle className="text-zinc-100">Session</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <Button 
                variant="destructive"
                onClick={handleLogout}
                className="bg-red-900/50 hover:bg-red-900 border border-red-800"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
              <p className="text-xs text-zinc-600 mt-2">
                This will end your session. You will need to login again.
              </p>
            </CardContent>
          </Card>

          {/* System Info */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-sm text-zinc-400">System Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2 text-xs text-zinc-600">
                <div className="flex justify-between">
                  <span>Version</span>
                  <span>v1.0.0</span>
                </div>
                <div className="flex justify-between">
                  <span>Environment</span>
                  <span>Private Workbench</span>
                </div>
                <div className="flex justify-between">
                  <span>Framework</span>
                  <span>Snap-In Capability Framework v1</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
